import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Loader2, Clock, DollarSign, Wallet, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getServices, createService, updateService, deleteService } from "@/lib/api";

export default function ServiceManagement() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [formData, setFormData] = useState({
    name: "",
    duration: "",
    base_price: "",
    staff_wage: "",
    payment_method: "Cash",
  });

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServices();
      setServices(data);
    } catch (error) {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const resetForm = () => {
    setFormData({
      name: "",
      duration: "",
      base_price: "",
      staff_wage: "",
      payment_method: "Cash",
    });
    setEditingService(null);
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        duration: service.duration.toString(),
        base_price: service.base_price.toString(),
        staff_wage: service.staff_wage.toString(),
        payment_method: service.payment_method || "Cash",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.duration || !formData.base_price || !formData.staff_wage) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const data = {
        name: formData.name,
        duration: parseInt(formData.duration),
        base_price: parseFloat(formData.base_price),
        staff_wage: parseFloat(formData.staff_wage),
        payment_method: formData.payment_method || "Cash",
      };

      if (editingService) {
        const updated = await updateService(editingService.id, data);
        setServices(prev => prev.map(s => s.id === editingService.id ? updated : s));
        toast.success("Service updated");
      } else {
        const created = await createService(data);
        setServices(prev => [...prev, created]);
        toast.success("Service created");
      }
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteService(deleteId);
      setServices(prev => prev.filter(s => s.id !== deleteId));
      toast.success("Service deleted");
    } catch (error) {
      toast.error("Failed to delete service");
    } finally {
      setDeleteId(null);
    }
  };

  const handleDuplicateToEftpos = async (service) => {
    const exists = services.some(
      (s) =>
        s.name === service.name &&
        s.duration === service.duration &&
        (s.payment_method || "Cash") === "Eftpos"
    );
    if (exists) {
      toast.info("An Eftpos variant already exists for this service");
      return;
    }
    try {
      const created = await createService({
        name: service.name,
        duration: service.duration,
        base_price: service.base_price,
        staff_wage: service.staff_wage,
        payment_method: "Eftpos",
      });
      setServices(prev => [...prev, created]);
      toast.success(`Created Eftpos variant of "${service.name}" — adjust its price if needed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate service");
    }
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest">Service & Wage Master</h2>
          <p className="text-sage">Configure services with pricing and staff wages</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-forest hover:bg-forest-light"
          data-testid="add-service-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Service
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-ochre/10 border border-ochre/20 rounded-xl p-4">
        <p className="text-sm text-forest">
          <strong>Wage Calculation:</strong> The staff wage set here is the therapist's commission for that
          service. It is pulled automatically whenever a session is recorded.
        </p>
      </div>

      {/* Services Table */}
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : services.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-sage">
            <p>No services found</p>
            <p className="text-sm">Add your first service to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="services-table">
              <TableHeader>
                <TableRow className="bg-sand">
                  <TableHead className="table-header-cell">Service Name</TableHead>
                  <TableHead className="table-header-cell text-center">Duration</TableHead>
                  <TableHead className="table-header-cell text-center">Payment</TableHead>
                  <TableHead className="table-header-cell text-right">Customer Price</TableHead>
                  <TableHead className="table-header-cell text-right">Staff Wage</TableHead>
                  <TableHead className="table-header-cell text-right">Profit Margin</TableHead>
                  <TableHead className="table-header-cell w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => {
                  const profitMargin = service.base_price - service.staff_wage;
                  const profitPercent = ((profitMargin / service.base_price) * 100).toFixed(0);
                  
                  return (
                    <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                      <TableCell className="font-medium">{service.name}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Clock className="h-4 w-4 text-sage" />
                          <span>{service.duration} min</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          (service.payment_method || "Cash") === "Eftpos"
                            ? "bg-forest/10 text-forest"
                            : "bg-ochre/15 text-ochre"
                        }`} data-testid={`service-payment-${service.id}`}>
                          {service.payment_method || "Cash"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium text-forest">
                        ${service.base_price.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-sage">
                        ${service.staff_wage.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={profitMargin > 0 ? "text-forest" : "text-destructive"}>
                          ${profitMargin.toFixed(2)}
                        </span>
                        <span className="text-xs text-sage ml-1">({profitPercent}%)</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {(service.payment_method || "Cash") !== "Eftpos" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-forest hover:text-forest"
                              onClick={() => handleDuplicateToEftpos(service)}
                              data-testid={`duplicate-eftpos-${service.id}`}
                              title="Duplicate to Eftpos"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleOpenDialog(service)}
                            data-testid={`edit-service-${service.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteId(service.id)}
                            data-testid={`delete-service-${service.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">
              {editingService ? "Edit Service" : "Add New Service"}
            </DialogTitle>
            <DialogDescription>
              {editingService 
                ? "Update the service details and wage"
                : "Create a new massage service with staff wage"
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Service Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Relax 60 min"
                data-testid="service-name-input"
              />
              <p className="text-xs text-sage">Use a clean name only — no need to add "- Cash" or "- Eftpos". Pick the payment method below instead.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">Duration (minutes) *</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.duration}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder="60"
                  data-testid="service-duration-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">Payment Method *</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}
                >
                  <SelectTrigger data-testid="service-payment-method-select">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Eftpos">Eftpos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest flex items-center gap-1">
                  <DollarSign className="h-4 w-4" />
                  Customer Price *
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.base_price}
                  onChange={(e) => setFormData(prev => ({ ...prev, base_price: e.target.value }))}
                  placeholder="80.00"
                  data-testid="service-price-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest flex items-center gap-1">
                  <Wallet className="h-4 w-4" />
                  Staff Wage *
                </Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.staff_wage}
                  onChange={(e) => setFormData(prev => ({ ...prev, staff_wage: e.target.value }))}
                  placeholder="35.00"
                  data-testid="service-wage-input"
                />
              </div>
            </div>

            {formData.base_price && formData.staff_wage && (
              <div className="bg-sand rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-sage">Profit per service:</span>
                  <span className="font-medium text-forest">
                    ${(parseFloat(formData.base_price) - parseFloat(formData.staff_wage)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="cancel-service-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-forest hover:bg-forest-light"
                disabled={submitting}
                data-testid="save-service-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingService ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this service? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-service-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-service-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
